'use client';

import { useEffect, useState } from 'react';
import { Database, X } from 'lucide-react';

import { cn } from '@/lib/util/cn';

/**
 * Persistent bottom-right `DEV · MOCK DATA` chip. Dismissible per-session
 * (sessionStorage, NOT localStorage — every fresh session re-asserts the
 * presence of mock data). Cannot be permanently hidden.
 *
 * Hidden under `NEXT_PUBLIC_SHOW_MOCK_BADGES=0` (set when the entire surface
 * runs against real backend).
 */
export function MockBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
    try {
      setDismissed(sessionStorage.getItem('te:mock-banner-dismissed') === '1');
    } catch {
      // Private browsing — keep visible.
    }
  }, []);

  if (process.env.NEXT_PUBLIC_SHOW_MOCK_BADGES === '0') {
    return null;
  }
  if (!hydrated || dismissed) {
    return null;
  }

  return (
    <div
      data-mock-banner="true"
      data-print="hide"
      className={cn(
        'fixed bottom-3 right-3 z-50 flex select-none items-center gap-2',
        'rounded-md border border-warn/60 bg-surface-elev/95 px-3 py-1.5',
        'text-xs font-medium text-warn shadow-lg backdrop-blur',
      )}
      role="status"
      aria-label="Development mock-data environment"
    >
      <Database aria-hidden className="h-3.5 w-3.5 stroke-[1.5]" />
      <span className="font-mono uppercase tracking-wider">dev · mock data</span>
      <button
        type="button"
        className="ml-1 rounded-sm p-0.5 text-fg-muted hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
        onClick={() => {
          try {
            sessionStorage.setItem('te:mock-banner-dismissed', '1');
          } catch {
            /* ignored */
          }
          setDismissed(true);
        }}
        aria-label="Dismiss mock-data banner for this session"
      >
        <X aria-hidden className="h-3.5 w-3.5 stroke-[1.5]" />
      </button>
    </div>
  );
}
